import { formatMXN } from "../../../lib/whatsapp";
import { X, Loader2 } from "lucide-react";

const PHASE_LABELS = {
  type: "Tipo de álbum", size: "Tamaño", pages: "Número de páginas",
  cover: "Tipo de portada", color: "Color", personalization: "Personalización",
};

const FIELD_BY_PHASE = { type: "type_id", size: "size_id", pages: "pages_id", cover: "cover_id", color: "color_id", personalization: "personalization_id" };

const OptionGrid = ({ options, selectedId, onSelect, testidPrefix }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid={`${testidPrefix}-options`}>
    {options.map((opt) => (
      <button
        key={opt.id}
        type="button"
        data-testid={`${testidPrefix}-option-${opt.id}`}
        onClick={() => onSelect(opt.id)}
        className={`border px-3 py-3 text-sm text-left transition-colors ${
          selectedId === opt.id ? "border-twp-gold bg-twp-gold/10 text-twp-gold" : "border-white/15 text-white/70 hover:border-twp-gold/50"
        }`}
      >
        <div>{opt.label}</div>
        {opt.subtitle && <div className="text-xs text-white/40 mt-0.5">{opt.subtitle}</div>}
      </button>
    ))}
  </div>
);

const Breadcrumbs = ({ config }) => {
  const chosen = ["type", "size", "pages", "cover", "color", "personalization"].filter((p) => config.sel[FIELD_BY_PHASE[p]]);
  if (!chosen.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-4" data-testid="album-breadcrumbs">
      {chosen.map((p) => {
        const id = config.sel[FIELD_BY_PHASE[p]];
        const label = config.attrById[id]?.label;
        return (
          <button
            key={p}
            type="button"
            data-testid={`album-breadcrumb-${p}`}
            onClick={() => config.editFrom(FIELD_BY_PHASE[p])}
            title="Quitar esta selección y elegir otra opción"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-twp-gold/40 text-twp-gold hover:bg-twp-gold/10"
          >
            {PHASE_LABELS[p]}: {label}
            <X size={12} className="text-twp-gold/70" data-testid={`album-breadcrumb-remove-${p}`} />
          </button>
        );
      })}
    </div>
  );
};

const AlbumSummary = ({ summary, onRemove }) => (
  <div className="border border-twp-gold/40 bg-twp-gold/5 p-4 space-y-2" data-testid="album-summary">
    <p className="text-white font-serif-display text-lg">Tu álbum personalizado</p>
    <ul className="text-sm text-white/70 space-y-1">
      <li>Tipo: {summary.typeLabel}</li>
      <li>Tamaño: {summary.sizeLabel}</li>
      <li>Páginas: {summary.pages}</li>
      <li>Portada: {summary.coverLabel}{summary.coverDescription ? ` (${summary.coverDescription})` : ""}</li>
      {summary.colorLabel && <li>Color: {summary.colorLabel}</li>}
      {summary.personalizationLabel && <li>Personalización: {summary.personalizationLabel}</li>}
      {summary.paperLabel && <li>Papel: {summary.paperLabel}</li>}
      {summary.laminateLabel && <li>Laminado: {summary.laminateLabel}</li>}
      {summary.featureNotes.map((note) => <li key={note}>{note}</li>)}
    </ul>
    <div className="flex items-center justify-between pt-2 border-t border-white/10">
      <span className="text-twp-gold text-xl font-serif-display" data-testid="album-summary-price">{formatMXN(summary.price)}</span>
      <button
        type="button"
        data-testid="album-remove-button"
        onClick={onRemove}
        className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300 border border-red-400/30 px-3 py-2"
      >
        <X size={14} /> Quitar álbum
      </button>
    </div>
  </div>
);

export const AlbumConfigurator = ({ config }) => {
  if (config.loading) {
    return <div className="flex items-center gap-2 text-white/50 text-sm py-4"><Loader2 className="animate-spin" size={16} /> Cargando catálogo de álbumes...</div>;
  }

  const phaseOptions = {
    type: config.typeOptions, size: config.sizeOptions, pages: config.pagesOptions,
    cover: config.coverOptions, color: config.colorOptions, personalization: config.personalizationOptions,
  };

  return (
    <div className="space-y-4 pt-2" data-testid="album-configurator">
      <Breadcrumbs config={config} />
      {config.phase === "summary" ? (
        <AlbumSummary summary={config.summary} onRemove={config.remove} />
      ) : (
        <div data-testid={`album-phase-${config.phase}`}>
          <p className="text-white/60 text-xs uppercase tracking-wider mb-2">{PHASE_LABELS[config.phase]}</p>
          <OptionGrid
            options={phaseOptions[config.phase]}
            selectedId={config.sel[FIELD_BY_PHASE[config.phase]]}
            onSelect={(id) => config.selectField(FIELD_BY_PHASE[config.phase], id)}
            testidPrefix={`album-${config.phase}`}
          />
        </div>
      )}
    </div>
  );
};
